import React from 'react'
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid'
import { NameDto } from 'api/Common/Types'
import {
  isCampaignCalculationAccountType,
  isCampaignCalculationTiersType,
} from 'api/Trading-Accounts/Types/CalculationType'
import { CampaignRebateDto } from 'api/Trading-Accounts/Types/MarketingCampaignDetailDto'
import AddButton from 'components/AddButton/AddButton'
import { FlexBox } from 'components/Box'
import {
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
} from 'components/Form/Form.styles'
import MoreButton from 'components/MoreButton/MoreButton'

import { CampaignRebateConfiguration } from './CampaignRebateConfigurationModal/CampaignRebateConfigurationModal'

interface CampaignRebateConfigurationTableProps {
  calculationTypeId: number | undefined
  rebates: CampaignRebateDto[]
  tradingAccountTypes: NameDto[]
  isEdit: boolean
  onAdd(): void
  onEdit(data: CampaignRebateConfiguration): void
  onRemove(data: CampaignRebateConfiguration): void
  className?: string
}

export const CampaignRebateConfigurationTable: React.FC<CampaignRebateConfigurationTableProps> = (
  props
) => {
  const { calculationTypeId, rebates, isEdit, onAdd, onEdit, onRemove } = props

  return (
    <FormBaseFieldBlock>
      <FormBaseFieldGroup style={{ flexDirection: 'column', alignItems: 'flex-end' }}>
        <FlexBox alignItems='center' justifyContent='space-between' marginTop={48} width='100%'>
          <FormBaseFieldLabel>Rebate Configuration</FormBaseFieldLabel>
          <AddButton disabled={!isEdit} text='Add Rebate Configurations' onClick={() => onAdd()} />
        </FlexBox>
        {isCampaignCalculationTiersType(calculationTypeId) && (
          <Grid data={rebates} resizable className={props.className}>
            <GridColumn field='rebateValue' title='Tier' />
            <GridColumn field='minimumVolume' title='Minimum Volume' />
            <GridColumn field='rebateMultiplier' title='Multiplier' />
            <GridColumn
              field='Action'
              cell={(x: GridCellProps) => {
                const data = x.dataItem as CampaignRebateConfiguration
                return (
                  <td>
                    <MoreButton
                      items={[
                        {
                          name: 'Edit',
                          onClick: () => onEdit(data),
                        },
                        {
                          name: 'Delete',
                          onClick: () => onRemove(data),
                        },
                      ]}
                    />
                  </td>
                )
              }}
            />
          </Grid>
        )}
        {isCampaignCalculationAccountType(calculationTypeId) && (
          <Grid data={rebates} resizable className={props.className}>
            <GridColumn
              field='campaignCalculationTypeId.id'
              title='Account Type'
              cell={(gridCellProps: GridCellProps) => {
                return <td>{getTradingAccountName(gridCellProps)(props)}</td>
              }}
            />
            <GridColumn field='rebateMultiplier' title='Multiplier' />
            <GridColumn
              field='Action'
              width={80}
              cell={(x: GridCellProps) => {
                const data = x.dataItem as CampaignRebateConfiguration
                return (
                  <td>
                    <MoreButton
                      items={[
                        {
                          name: 'Edit',
                          onClick: () => onEdit(data),
                          disabled: !isEdit,
                        },
                        {
                          name: 'Delete',
                          onClick: () => onRemove(data),
                          disabled: !isEdit,
                        },
                      ]}
                    />
                  </td>
                )
              }}
            />
          </Grid>
        )}
      </FormBaseFieldGroup>
    </FormBaseFieldBlock>
  )
}

const getTradingAccountName =
  (gridCellProps: GridCellProps) => (props: CampaignRebateConfigurationTableProps) => {
    const { tradingAccountTypes } = props
    const dataItem = gridCellProps.dataItem as CampaignRebateDto
    const [tradingAccountType] = tradingAccountTypes.filter((x) => x.id === dataItem.rebateValue)
    return tradingAccountType?.name || ''
  }
